'use client';

import { useTranslation } from '@/hooks/useTranslation';
import BookDemoSection from '@/components/BookDemoSection';
import { FaqAccordion, PageSection, SectionTitle } from '@/components/ui';
import { MetricsBand } from '@/components/primitives/MetricsBand';
import RetailHero from './RetailHero';
import IndustryBattleground from '@/components/industry/IndustryBattleground';
import IndustryChallenges from '@/components/industry/IndustryChallenges';
import PlatformCapabilities from '@/components/industry/PlatformCapabilities';
import KeyCapabilitiesGrid from '@/components/industry/KeyCapabilitiesGrid';
import IndustryResults from '@/components/industry/IndustryResults';
import { getIndustryContent } from '@/lib/industryContent';
import '@/components/industry/industry-page.css';

/* Problem-framing stats for the MetricsBand — sourced from IBEF, nShift /
   Retail4Growth, and ServiceNow Report (previously displayed in the hero). */
const METRICS = [
  { value: '$400B', label: "India's e-commerce market in 2025 — projected to cross $500B by 2027 (IBEF)" },
  { value: '20%+', label: 'of shoppers complain on social media after a bad experience (nShift / Retail4Growth)' },
  { value: '15B hrs', label: 'Spent by Indian consumers waiting to resolve complaints in 2024 (ServiceNow Report)' },
];

export default function RetailBody() {
  const { t } = useTranslation();
  const C = getIndustryContent('retail-brands');

  const CHALLENGES = [
    {
      title: t("industries_retail_ecommerce.the_festive_sale_avalanche"),
      desc: t("industries_retail_ecommerce.big_billion_days_great_indian_festi"),
    },
    {
      title: t("industries_retail_ecommerce.the_return_refund_flood"),
      desc: t("industries_retail_ecommerce.refund_delay_complaints_are_among_t"),
    },
    {
      title: t("industries_retail_ecommerce.the_counterfeit_crisis"),
      desc: t("industries_retail_ecommerce.counterfeit_listings_and_product_qu"),
    },
    {
      title: t("industries_fmcg.the_influencer_blindspot"),
      desc: t("industries_retail_ecommerce.a_micro_influencers_negative_unboxi"),
    },
  ];

  const FIFTH_CHALLENGE = {
    title: t("industries_retail_ecommerce.the_10_minute_expectation"),
    desc: t("industries_retail_ecommerce.quick_commerce_customer_support_too"),
  };

  const FAQS = [
    {
      q: t("industries_retail_ecommerce.what_is_an_ecommerce_customer_exper"),
      a: t("industries_retail_ecommerce.one_workspace_for_delivery_complain"),
    },
    {
      q: t("industries_retail_ecommerce.how_does_locobuzz_manage_the_spike"),
      a: t("industries_retail_ecommerce.crisis_mode_pre_configured_before_t"),
    },
    {
      q: t("industries_retail_ecommerce.can_locobuzz_help_d2c_brands_monito"),
      a: t("industries_retail_ecommerce.yes_social_listening_tracks_all_bra"),
    },
    {
      q: t("industries_retail_ecommerce.what_are_contextualpulse_and_signal"),
      a: t("industries_retail_ecommerce.contextualpulse_identifies_whether"),
    },
    {
      q: t("industries_retail_ecommerce.how_does_locobuzz_support_quick_com"),
      a: t("industries_retail_ecommerce.real_time_monitoring_configurable_a"),
    },
    {
      q: t("industries_retail_ecommerce.can_locobuzz_monitor_product_review"),
      a: t("industries_retail_ecommerce.yes_aggregated_from_amazon_india_fl"),
    },
    {
      q: t("industries_retail_ecommerce.does_locobuzz_support_compliance_wi"),
      a: t("industries_retail_ecommerce.yes_designated_complaint_officer_wo"),
    },
    {
      q: t("industries_retail_ecommerce.how_quickly_can_a_retail_or_ecommer"),
      a: t("industries_retail_ecommerce.most_retail_brands_go_live_within_2"),
    },
  ];

  const BG_TITLE = t('industries_retail_ecommerce.why_is_indias_ecommerce_boom_creati');
  const BG_ACCENT = 'Customer Experience Crisis?';
  const _bgCut = BG_TITLE.lastIndexOf(BG_ACCENT);
  const bgTitle = _bgCut > 0 ? BG_TITLE.slice(0, _bgCut).trim() : BG_TITLE;
  const bgAccent = _bgCut > 0 ? BG_TITLE.slice(_bgCut) : undefined;

  return (
    <div className="industry-page-wrap">
      {/* AEO conversational seeds — in HTML source, never rendered visibly */}
      <span className="sr-only">
        {t("industries_retail_ecommerce.locobuzz_is_an_indian_ainative_cust")}
      </span>
      <span className="sr-only">
        {t("industries_retail_ecommerce.indias_400_billion_ecommerce_market")}
      </span>
      <span className="sr-only">
        {t("industries_retail_ecommerce.locobuzz_maps_six_purposebuilt_capa")}
      </span>
      <span className="sr-only">
        {t("industries_retail_ecommerce.retail_and_ecommerce_brands_using_l")}
      </span>

      <RetailHero />

      <MetricsBand items={METRICS} columns={3} />

      <IndustryBattleground
        eyebrow={t("industries_retail_ecommerce.why_ecommerce_is_a_cx_crisis")}
        title={bgTitle}
        titleAccent={bgAccent}
        paragraph={t("industries_retail_ecommerce.during_peak_events_like_big_billion")}
        imageSrc="/industries/why/retail-brands.webp"
        imageAlt={t("industries_retail_brands.retail_and_ecommerce_social_engagem")}
        bg="tint"
      />

      <IndustryChallenges
        title={t("industries_retail_ecommerce.what_challenges_do_retail_ecommerce")}
        items={[...CHALLENGES, FIFTH_CHALLENGE]}
        layout="row5"
        align="center"
        bg="page"
      />

      <PlatformCapabilities {...C.capabilities} />

      <KeyCapabilitiesGrid {...C.keyGrid} layout="grid" bg="page" />

      <IndustryResults {...C.results} />

      <PageSection id="faqs" bg="tint">
        <SectionTitle
          title={
            <>
              {t('faq.everything_you_need_to')}{' '}
              <span className="serif-accent italic">{t('faq.know')}</span>
            </>
          }
          align="center"
        />
        <FaqAccordion items={FAQS} columns={2} name="retail-faq" />
      </PageSection>

      <BookDemoSection />
    </div>
  );
}
